/**
 * S3 Presigned POST upload helper
 *
 * The backend returns `{ url, fields, object_key }` from its /upload-url endpoints.
 * This helper turns that payload into an actual upload to S3 via multipart/form-data.
 *
 * We intentionally do NOT go through apiClient here — the S3 POST has its own
 * CORS rules, no auth cookies, and is a different origin from our API.
 */

export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
  object_key: string;
}

/**
 * Upload a File to S3 using a presigned POST payload.
 * Throws if the S3 upload returns a non-2xx status.
 */
export async function uploadToS3Presigned(presigned: PresignedPost, file: File): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(presigned.fields)) {
    formData.append(key, value);
  }
  formData.append('file', file);

  const response = await fetch(presigned.url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `S3 upload failed for ${file.name}: ${response.status} ${response.statusText}${
        text ? ` — ${text.slice(0, 200)}` : ''
      }`,
    );
  }
}
